/**
 * AddExpense -- anyone on the team records an expense / disbursement as they
 * go, against a matter, from the left nav under My Time.
 *
 * BM-H3620 (5 Oct 2026), Vanessa: "under My Time on the left, we need an Add
 * expense/disbursement for the team to add as they go". Writes to the same
 * native disbursements ledger as everything else (POST /disbursements), so
 * what is entered here is what invoicing picks up.
 *
 * Two rules from the ledger are carried into the form rather than hidden:
 *   - VAT treatment is required and never defaulted (a wrong guess lands on
 *     a client invoice), so the select starts on a placeholder.
 *   - Bank transfer fees (CHAPS, Faster Payment), search packs and ID/AML
 *     checks are Other Fees under the SRA Accounts Rules, not disbursements
 *     (BM-H3424); the page says so beside the description.
 * Delete is allowed only while an entry is still a draft, same as time entries.
 *
 * BM-H4539 (20 Oct 2026): May (accounts) added an onboarding fee to the wrong
 * matter's expenses and could not find a way to take it off. "Expenses on a
 * matter" lists every entry on one matter, and the bin shows wherever the
 * server says the viewer may remove it (the person who added it, accounts, a
 * director), never on an invoiced entry. Removal takes a second click to
 * confirm, comes off any invoice draft not yet issued, and is written to the
 * matter's Activity panel by the server. ?matter=REF opens straight onto a
 * matter, which is how the invoice editor links here.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Receipt, Trash2, Check, X } from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import ExpenseCategoryPicker from "../components/ExpenseCategoryPicker";

const FIELD = "w-full bg-transparent border border-navy-border rounded px-3 py-2 text-sm text-white placeholder:text-slate-600";
const LABEL = "block text-sm font-medium text-teal mb-1";
const VOID_REASON = "Added in error";

const VAT_OPTIONS = [
  { value: "no_vat", label: "No VAT (outside the scope / exempt)" },
  { value: "inc_vat", label: "Amount includes VAT" },
  { value: "plus_vat", label: "VAT to be added on top" },
];
const VAT_LABEL = Object.fromEntries(VAT_OPTIONS.map((o) => [o.value, o.label]));

const STATUS_LABEL = { draft: "Not yet invoiced", billed: "Invoiced", void: "Removed" };

const fmtAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "£0.00";
};

function matterFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get("matter") || "";
  } catch {
    return "";
  }
}

function ExpenseTable({ rows, prefix, removingId, confirmId, onAskRemove, onCancelRemove, onRemove, showAddedBy }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid={`${prefix}-table`}>
        <thead>
          <tr className="border-b border-navy-border text-slate-500">
            <th className="px-2 py-2 text-left">Matter</th>
            <th className="px-2 py-2 text-left">Description</th>
            <th className="px-2 py-2 text-left">Category</th>
            <th className="px-2 py-2 text-right">Amount</th>
            <th className="px-2 py-2 text-left">Status</th>
            {showAddedBy && <th className="px-2 py-2 text-left">Added by</th>}
            <th className="px-2 py-2 w-16"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id} className="border-b border-navy-border/40" data-testid={`${prefix}-row`}>
              <td className="px-2 py-2 font-mono text-white">{d.matter_ref}</td>
              <td className="px-2 py-2 text-slate-300 max-w-xs truncate" title={d.description}>{d.description}</td>
              <td className="px-2 py-2 text-slate-400">{d.category || ""}</td>
              <td className="px-2 py-2 text-right text-white" title={VAT_LABEL[d.vat_treatment] || ""}>
                {fmtAmount(d.amount)}
                {d.chargeable === false && <span className="ml-1 text-[10px] text-amber-400">(not charged)</span>}
              </td>
              <td className="px-2 py-2 text-slate-400">{STATUS_LABEL[d.status] || d.status}</td>
              {showAddedBy && <td className="px-2 py-2 text-slate-400">{(d.created_by || "").split("@")[0]}</td>}
              <td className="px-2 py-2 whitespace-nowrap text-right">
                {d.can_remove && confirmId !== d.id && (
                  <button
                    type="button" onClick={() => onAskRemove(d)} disabled={removingId === d.id}
                    className="text-slate-400 hover:text-red-400 p-1 disabled:opacity-50"
                    title="Remove" aria-label="Remove" data-testid={`${prefix}-remove-${d.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {d.can_remove && confirmId === d.id && (
                  <span className="inline-flex items-center gap-1">
                    <button
                      type="button" onClick={() => onRemove(d)} disabled={removingId === d.id}
                      className="text-red-400 hover:text-red-300 p-1 disabled:opacity-50"
                      title="Yes, remove it" aria-label="Yes, remove it" data-testid={`${prefix}-confirm-${d.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button" onClick={onCancelRemove}
                      className="text-slate-400 hover:text-white p-1"
                      title="Keep it" aria-label="Keep it" data-testid={`${prefix}-cancel-${d.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BLANK = { matterRef: "", matterLabel: "", description: "", amount: "", vat: "", category: "", chargeable: true };

export default function AddExpense() {
  const [form, setForm] = useState(BLANK);
  const [searchKey, setSearchKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [lookupRef, setLookupRef] = useState(matterFromUrl);
  const [onMatter, setOnMatter] = useState([]);
  const [loadingMatter, setLoadingMatter] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/disbursement-categories`, { withCredentials: true });
      setCategories(r.data?.categories || []);
    } catch (err) {
      // Not fatal: the box still accepts a typed category.
      toast.error(errorMessage(err, "Couldn't load the category list."));
    }
  }, []);

  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const r = await axios.get(`${API}/disbursements`, { params: { mine: true }, withCredentials: true });
      setMine(r.data?.disbursements || []);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load your expenses."));
    }
    setLoadingMine(false);
  }, []);

  const loadMatter = useCallback(async (ref) => {
    if (!ref) {
      setOnMatter([]);
      return;
    }
    setLoadingMatter(true);
    try {
      const r = await axios.get(`${API}/disbursements`, { params: { matter_ref: ref }, withCredentials: true });
      setOnMatter(r.data?.disbursements || []);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load that matter's expenses."));
    }
    setLoadingMatter(false);
  }, []);

  useEffect(() => { loadCategories(); loadMine(); }, [loadCategories, loadMine]);
  useEffect(() => { loadMatter(lookupRef); }, [loadMatter, lookupRef]);

  const handleMatterSelect = (result) => {
    setForm((f) => ({
      ...f,
      matterRef: result?.matter_reference || "",
      matterLabel: result?.label || result?.matter_reference || "",
    }));
  };

  const amountNum = Number(form.amount);
  const missing = [];
  if (!form.matterRef) missing.push("a matter");
  if (!form.description.trim()) missing.push("a description");
  if (!(amountNum > 0)) missing.push("an amount");
  if (!form.vat) missing.push("the VAT treatment");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (missing.length) {
      toast.error(`Please add ${missing.join(", ")}.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        matter_ref: form.matterRef,
        description: form.description.trim(),
        amount: amountNum,
        vat_treatment: form.vat,
        chargeable: form.chargeable,
      };
      if (form.category.trim()) payload.category = form.category.trim();
      await axios.post(`${API}/disbursements`, payload, { withCredentials: true });
      toast.success("Expense added.");
      setForm(BLANK);
      setSearchKey((k) => k + 1); // remount the matter search to clear it
      loadMine();
      loadCategories(); // a new category is now in the list for everyone
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save that expense."));
    }
    setSaving(false);
  };

  const tableProps = {
    removingId, confirmId,
    onAskRemove: (d) => setConfirmId(d.id),
    onCancelRemove: () => setConfirmId(null),
  };

  const handleRemove = async (d) => {
    setRemovingId(d.id);
    try {
      await axios.post(`${API}/disbursements/${d.id}/void`, { reason: VOID_REASON }, { withCredentials: true });
      toast.success("Expense removed.");
      loadMine();
      loadMatter(lookupRef);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't remove that expense."));
    }
    setRemovingId(null);
    setConfirmId(null);
  };

  const visibleMine = mine.filter((d) => d.status !== "void");
  const visibleOnMatter = onMatter.filter((d) => d.status !== "void");

  return (
    <div className="min-h-screen" style={{ background: "#060e18" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 0" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f9fafb", margin: 0 }} className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-teal" />
          Add expense
        </h1>
        <p className="text-sm text-slate-400 mt-2">
          Record a cost you have paid on a matter, as you go, so it is on the file when the invoice is raised.
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 80px" }}>
        <form
          onSubmit={handleSubmit}
          className="bg-white/[0.02] border border-white/10 rounded-lg p-5 mb-6 space-y-4"
          data-testid="add-expense-form"
        >
          <SmartClientMatterSearch
            key={searchKey}
            label="Matter"
            placeholder="Search matters..."
            mattersOnly
            onSelect={handleMatterSelect}
            initialValue={form.matterLabel}
          />

          <div>
            <label className={LABEL} htmlFor="expense-description">Description</label>
            <input
              id="expense-description" type="text" value={form.description} onChange={set("description")}
              placeholder="e.g. Taxi to court, courier to counsel"
              className={FIELD} data-testid="expense-description"
            />
            <p className="mt-1 text-xs text-slate-500" data-testid="expense-other-fees-note">
              Bank transfer fees (CHAPS, Faster Payment), search packs and ID / anti-money-laundering checks are
              firm fees, not expenses. Don&rsquo;t add those here.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL} htmlFor="expense-amount">Amount (£)</label>
              <input
                id="expense-amount" type="number" inputMode="decimal" min="0" step="0.01"
                value={form.amount} onChange={set("amount")} placeholder="0.00"
                className={FIELD} data-testid="expense-amount"
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="expense-vat">VAT</label>
              <select
                id="expense-vat" value={form.vat} onChange={set("vat")}
                className={FIELD} data-testid="expense-vat"
              >
                <option value="" className="bg-navy-card">Choose VAT treatment</option>
                {VAT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-navy-card">{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <span className={LABEL}>Category</span>
            <ExpenseCategoryPicker
              categories={categories}
              value={form.category}
              onChange={(v) => setForm((f) => ({ ...f, category: v }))}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox" checked={!form.chargeable}
              onChange={(e) => setForm((f) => ({ ...f, chargeable: !e.target.checked }))}
              data-testid="expense-dont-charge"
            />
            Don&rsquo;t charge this to the client
          </label>

          <div className="flex items-center gap-3">
            <button
              type="submit" disabled={saving}
              className="px-4 py-2 rounded bg-teal text-navy font-medium text-sm disabled:opacity-50"
              data-testid="expense-submit"
            >
              {saving ? "Saving..." : "Add expense"}
            </button>
            {missing.length > 0 && (
              <span className="text-xs text-slate-500" data-testid="expense-missing">
                Still needed: {missing.join(", ")}
              </span>
            )}
          </div>
        </form>

        <section className="bg-white/[0.02] border border-white/10 rounded-lg p-5" data-testid="my-expenses-card">
          <h2 className="text-base font-semibold text-white mb-4">My expenses</h2>
          {loadingMine ? (
            <div className="text-sm text-slate-500" data-testid="my-expenses-loading">Loading...</div>
          ) : visibleMine.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="my-expenses-empty">No expenses added yet.</div>
          ) : (
            <ExpenseTable rows={visibleMine} prefix="my-expense" onRemove={handleRemove} {...tableProps} />
          )}
        </section>

        <section className="bg-white/[0.02] border border-white/10 rounded-lg p-5 mt-6" data-testid="matter-expenses-card">
          <h2 className="text-base font-semibold text-white mb-1">Expenses on a matter</h2>
          <p className="text-xs text-slate-500 mb-3">
            Pick a matter to see every expense on it. Accounts and directors can remove one added in error, if it is not yet invoiced.
          </p>
          <SmartClientMatterSearch
            label="Find a matter"
            placeholder="Search matters..."
            mattersOnly
            onSelect={(r) => setLookupRef(r?.matter_reference || "")}
            initialValue={lookupRef}
          />
          {lookupRef && (
            <div className="mt-4">
              {loadingMatter ? (
                <div className="text-sm text-slate-500" data-testid="matter-expenses-loading">Loading...</div>
              ) : visibleOnMatter.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="matter-expenses-empty">No expenses on {lookupRef}.</div>
              ) : (
                <ExpenseTable rows={visibleOnMatter} prefix="matter-expense" onRemove={handleRemove} showAddedBy {...tableProps} />
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
